import { DynamicPropertyType } from 'types/DynamicPropertyType';

import { toFiniteNumber, transformToNumber } from '../parse';
import { makeCSVImportError } from '../tree';
import { DynamicProperty } from './Property';

/**
 * A plain CSS <number>. Related to the UnitNumber family but deliberately not
 * sharing a base with it: those are <length> / <percentage> and parse with
 * parseUnitValue, which requires a unit except for zero, whereas this accepts a
 * bare number and nothing else. They also serialise differently - this returns
 * numbers from getPreviewData and getCSVData where those return strings.
 */
export default class Number extends DynamicProperty<number> {
  type = DynamicPropertyType.NUMBER;
  value: number = 0;

  setDefault = () => {
    this.setInitialValue(0);
  };

  transform = (value: any): number => transformToNumber(value, this);

  // The guard keeps an undefined value from stringifying as "undefined";
  // matches UnitNumber.getString, minus the unit suffix.
  getString = () => {
    if (!this.value && this.value !== 0) return '0';
    return this.value + '';
  };

  getPreviewData = () => {
    return this.value;
  };

  getCSVData = (): number => {
    return this.value;
  };

  setUsingCSV = (value: string) => {
    // Strict: a unitless <number> property should reject "12px" rather than
    // quietly reading 12 out of it, which is what parseFloat did here.
    const newValue = toFiniteNumber(value);
    if (newValue === undefined) {
      throw makeCSVImportError(
        this,
        `Not a number. Value: ${JSON.stringify(value)}`
      );
    }
    this.set(newValue);
  };
}
